import { BLANK_VALUE, formatMeasurement } from "./formatters";
import { CarbonUnit, UnitNumberType } from "../../utils/units/units";
import { classes } from "../../theme/theme";
import { CycleSlot } from "../../utils/metrics";
import { CycleTooltip } from "./CycleTooltip";
import { getDecimalPlacesForCycleSlot } from "./numericUnits";
import { isNil } from "../../utils/identity";
import { UnitSpan, useSavedUnit } from "./Unit";
import { useSelf } from "../../state/store";
import { useTranslation } from "react-i18next";
import React, { FC, ReactNode } from "react";

/**
 * @param cycleSlot the cycleSlot used to determine the units shown
 * @param unit the unit that the given value is in
 * @param value the value to be displayed
 *
 * @returns the given value, converted to the user's preferred unit, with the unit displayed
 **/
export const NumericMeasurement: FC<{
  cycleSlot?: CycleSlot;
  label?: "long" | "none";
  unit: CarbonUnit;
  value: number;
}> = ({ cycleSlot, label, unit, value }): ReactNode => {
  const { t, i18n } = useTranslation();
  const { measurementSystem } = useSelf();

  const { unit: toUnit, units } = useSavedUnit(cycleSlot, unit);

  const formatted = formatMeasurement(t, i18n, measurementSystem, value, unit, {
    decimalPlaces: getDecimalPlacesForCycleSlot(cycleSlot, toUnit),
    toUnits: toUnit,
    unitsDelimiter:
      (toUnit === UnitNumberType.PERCENT && !i18n.language.startsWith("fr")) ||
      cycleSlot === CycleSlot.DENSITY
        ? ""
        : " ",
  });

  return (
    <div
      className="unset flex items-center justify-end border-0 hover:border-b hover:border-b-dashed"
      style={{ overflowWrap: "anywhere" }}
    >
      <span
        className={classes("flex-grow leading-none min-w-1 flex items-center", {
          "opacity-50": !formatted.value || formatted.value === BLANK_VALUE,
        })}
      >
        {formatted.value || BLANK_VALUE}
      </span>

      {label !== "none" ? (
        <>
          {formatted.unitsDelimiter === " " ? (
            <span>&nbsp;</span>
          ) : formatted.unitsDelimiter !== "" ? (
            <span className="text-xs opacity-50">
              {formatted.unitsDelimiter}
            </span>
          ) : undefined}

          {!isNil(units) && units.length > 1 && !isNil(cycleSlot) ? (
            <CycleTooltip cycle={units} cycleSlot={cycleSlot}>
              <UnitSpan unit={toUnit} />
            </CycleTooltip>
          ) : (
            <UnitSpan unit={toUnit} />
          )}
        </>
      ) : undefined}
    </div>
  );
};
